import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons'
import type { SearchTileLogo } from '../SearchTile'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineJourney from '../shared/SearchTileRowOverlineJourney'
import CruiseSearchTileFeatures from './CruiseSearchTileFeatures'
import CruiseSearchTileLabels from './CruiseSearchTileLabels'
import CruiseSearchTileMeta from './CruiseSearchTileMeta'
import CruiseSearchTilePriceStack from './CruiseSearchTilePriceStack'
import CruiseImageCornerLabels from './CruiseImageCornerLabels'
import TextButton from 'LuxKit/Button/TextButton'
import useCruiseSelectedCabinType from 'hooks/useCruiseSelectedCabinType'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { trimCruiseShipNameFromTitle } from 'lib/cruises/cruiseUtils'
import { getCheapestCruiseFlashOfferByCabin } from 'lib/cruises/getCheapestCruiseFlashOfferByCabin'
import { hasDisplayablePackagePrice, isDisplayablePrice } from 'lib/offer/pricingVisibility'

interface Props {
  offer: App.CruiseOffer | App.TourOffer | App.TourOfferSummary
  filters?: App.OfferListFilters
}

function CruiseOfferTile({ offer, filters }: Props) {
  const intl = useIntl()
  const selectedCabinType = useCruiseSelectedCabinType(isCruiseOffer(offer) ? offer.cruiseType : undefined)
  const view = useCruiseOfferView({ offer, cabinTypes: selectedCabinType || undefined, filters })

  const logo = useMemo((): SearchTileLogo => ({
    compact: { id: view.cruiseLineImage, title: view.cruiseLineName },
    full: { id: view.cruiseLineImage, title: view.cruiseLineName },
  }), [view])

  const title = useMemo(() => trimCruiseShipNameFromTitle(offer.name), [offer.name])

  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const packagePricing = isCruiseOffer(offer) ? undefined : getCheapestCruiseFlashOfferByCabin({ offer, cabinType: selectedCabinType || '' }) || offer.lowestPricePackage
  const showPricingBlock = isCruiseOffer(offer) ?
      isDisplayablePrice(view.lowestPrice.promotionalPrice?.total ?? view.lowestPrice.primaryPrice.total) :
      hasDisplayablePackagePrice(packagePricing)
  const viewOfferLabel = intl.formatMessage({
    id: '1TDNDgzK3pnQZCGqQ7VW',
    defaultMessage: 'View offer',
  })
  return <SearchTile
    banner={<>{offerSiteTakeover?.searchPage?.tileBanner}</>}
    action={<TextButton nonInteractable kind="primary">{viewOfferLabel}</TextButton>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    features={<CruiseSearchTileFeatures view={view} filters={filters} />}
    images={offer.images}
    labels={<CruiseSearchTileLabels view={view} />}
    logo={logo}
    meta={<CruiseSearchTileMeta view={view} />}
    overline={<SearchTileRowOverlineJourney start={view.departureCity} end={view.destinationCity} />}
    pricePoint={showPricingBlock && <CruiseSearchTilePriceStack view={view} />}
    productLabel={<CruiseImageCornerLabels view={view} countdownTagPlace="offer-search-tile" />}
    title={title}
    offer={offer}
  />
}

export default CruiseOfferTile
